import type { JobStatusPayload, JobCreationMessageStatus } from '@techgitter/contracts';
import { onSignalREvent } from './signalRService';

const TERMINAL_STATUSES: JobCreationMessageStatus[] = ['Succeeded', 'Failed', 'Dead Letter'];

/**
 * How long to wait for a verdict before releasing the listener. Generous — a
 * RepairShopr round-trip plus the Dataverse write is normally a second or two,
 * and the only cost of waiting is one bound handler.
 */
export const DEFAULT_STATUS_RESULT_TIMEOUT_MS = 60_000;

export interface WatchJobStatusOptions {
  /** Called once, with the orchestrator's reason, if the operation failed. */
  onFailure: (reason: string, payload: JobStatusPayload) => void;
  /** Called once if it succeeded. Optional — most callers already acted optimistically. */
  onSuccess?: (payload: JobStatusPayload) => void;
  timeoutMs?: number;
  /** Shown when the orchestrator reports a failure but carries no message. */
  fallbackReason?: string;
}

/**
 * Watch for the orchestrator's verdict on one queued message.
 *
 * Endpoints that hand work to the orchestrator answer 202 as soon as it is
 * queued, so the request promise settles well before anything has been
 * written. Anything that can still go wrong after that point — RepairShopr
 * refusing a status change because a worksheet is incomplete, say — arrives
 * only on `jobs:status`. Without correlating it, the UI reverts its optimistic
 * update with no explanation and the user simply tries again.
 *
 * Request-scoped by design: it subscribes for one message id and releases the
 * handler on the first terminal verdict, or on timeout if the broadcast is
 * dropped. Returns an unsubscribe for callers that unmount first.
 */
export function watchJobStatusResult(
  messageId: string,
  {
    onFailure,
    onSuccess,
    timeoutMs = DEFAULT_STATUS_RESULT_TIMEOUT_MS,
    fallbackReason = 'The change was rejected, but no reason was given.',
  }: WatchJobStatusOptions,
): () => void {
  let settled = false;

  const finish = () => {
    if (settled) return false;
    settled = true;
    clearTimeout(timer);
    unsubscribe();
    return true;
  };

  const unsubscribe = onSignalREvent('jobs:status', (data: unknown) => {
    const payload = data as JobStatusPayload | undefined;
    if (!payload || payload.messageId !== messageId) return;
    if (!TERMINAL_STATUSES.includes(payload.status)) return;
    if (!finish()) return;

    if (payload.status === 'Succeeded') {
      onSuccess?.(payload);
      return;
    }

    // `errors` carries RepairShopr's own wording where there is any — it is
    // written for the person reading it ("complete the worksheet first"), so
    // it beats anything this layer could compose.
    onFailure(payload.errors?.find(e => e?.trim()) ?? fallbackReason, payload);
  });

  // A dropped broadcast must not leave a handler bound for the life of the
  // page. Staying silent is the right failure: the optimistic state stands and
  // the next refresh reconciles it.
  const timer = setTimeout(finish, timeoutMs);

  return () => { finish(); };
}
